import { createSlice } from '@reduxjs/toolkit';
import {
  getLocalStorageAuthentication,
  saveJWTInfoToLocalStorage
} from '../../../lib/auth';
import {
  BASE_END_POINT_USER,
  JOB_END_POINT,
  USER_END_POINT
} from '../../../config';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';
import { trackLogin, trackSignUp } from '../../../helpers';

// Array of friendly, motivating welcome messages for companies
const COMPANY_WELCOME_MESSAGES = [
  "🎉 Welcome back! Ready to find your next rockstar team member?",
  "✨ Great to see you! Your next amazing hire is just a post away!",
  "🚀 Welcome aboard! Let's build something incredible together today!",
  "💼 Hey there! Time to connect with world-class remote talent!",
  "🌟 You're in! Let's turn your job opening into the perfect match!",
  "👋 Welcome back, superstar! Ready to grow your dream team?",
  "🎯 Logged in and ready to win! Post a job and watch the magic happen!",
  "💪 You're all set! Let's find exceptional talent for your company!",
  "🔥 Welcome back! Your future team member is out there waiting!",
  "⚡ Success! Time to supercharge your team with top remote talent!",
  "🌍 Hello again! Connect with brilliant minds from around the globe!",
  "🎊 You're back! Let's make today the day you find THE perfect candidate!",
  "💎 Welcome! Your company deserves the best—let's find them together!",
  "🏆 You're in! Champions hire champions. Ready to build your A-team?",
  "🎪 Great to see you! The remote talent show is waiting for your audition!",
  "🚢 All aboard! Let's navigate the sea of talent and find your treasure!",
  "🎨 Welcome back, creator! Time to paint your masterpiece team!",
  "🌈 Success! Your rainbow of remote talent opportunities starts now!",
  "🎸 You're plugged in! Let's compose your perfect team harmony!",
  "🔮 Welcome back, visionary! Your future hire is closer than you think!"
];

// Array of friendly, motivating welcome messages for candidates
const CANDIDATE_WELCOME_MESSAGES = [
  "🎉 Welcome back! Your dream remote job is waiting for you!",
  "✨ Great to see you! Today could be the day you land your perfect role!",
  "🚀 Welcome aboard! Let's launch your remote career to new heights!",
  "💼 Hey there, talent! Amazing opportunities are just a click away!",
  "🌟 You're in! Let's match your skills with the perfect remote opportunity!",
  "👋 Welcome back, champion! Ready to discover your next adventure?",
  "🎯 Logged in and ready to win! Your ideal job match is out there!",
  "💪 You're all set! Let's find the company that deserves your talent!",
  "🔥 Welcome back! Your future employer is searching for someone like you!",
  "⚡ Success! Time to supercharge your career with amazing remote opportunities!",
  "🌍 Hello again! Explore opportunities from companies around the globe!",
  "🎊 You're back! Let's make today the day you find THE perfect job!",
  "💎 Welcome! You deserve the best—let's find your dream role together!",
  "🏆 You're in! Top companies are looking for A-players like you!",
  "🎪 Great to see you! The world of remote work is your stage!",
  "🚢 All aboard! Let's navigate your journey to career success!",
  "🎨 Welcome back, creator! Time to paint your career masterpiece!",
  "🌈 Success! Your rainbow of remote opportunities starts now!",
  "🎸 You're plugged in! Let's find the job that hits all the right notes!",
  "🔮 Welcome back, rising star! Your next opportunity is closer than you think!"
];

// Function to get a random welcome message based on user role
const getRandomWelcomeMessage = (userRoles) => {
  const isCandidate = userRoles?.includes('ROLE_CANDIDATE');
  const messages = isCandidate ? CANDIDATE_WELCOME_MESSAGES : COMPANY_WELCOME_MESSAGES;
  const randomIndex = Math.floor(Math.random() * messages.length);
  return messages[randomIndex];
};

const initialState = {
  message: '',
  token: null,
  isLogin: false,
  user: null,
  loading: false,
  error: null
};

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    userActionRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    userActionFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload.error;
    },
    loginSucceeded: (state, action) => {
      const token = action.payload.token;
      const user = action.payload.user;
      saveJWTInfoToLocalStorage(token);
      state.token = token;
      state.isLogin = true;
      state.user = user;
      state.loading = false;
      state.error = null;
    },
    logoutRequested: (state) => {
      state.isLogin = false;
      state.token = null;
      state.user = null;
      state.loading = false;
      state.error = null;
    },
    userInfoUpdated: (state, action) => {
      state.user = action.payload.user;
      state.loading = false;
      state.error = null;
    },
    registrationSucceeded: (state, action) => {
      saveJWTInfoToLocalStorage(action.payload.token);
      state.token = action.payload.token;
      state.isLogin = true;
      state.user = action.payload.user;
      state.loading = false;
      state.error = null;
    }
  }
});

export const {
  userActionRequest,
  userActionFailure,
  loginSucceeded,
  logoutRequested,
  userInfoUpdated,
  registrationSucceeded
} = usersSlice.actions;

export default usersSlice.reducer;

export const currentTokenSelector = (state) =>
  state.user && state.users.token ? state.users.token : null;
export const isLoginSelector = (state) => state.users.isLogin;
export const userSelector = (state) => state.users.user;
export const isOkRegistrationSelector = (state) => state.users.isOkRegistration;
export const userLoadingSelector = (state) => state.users.loading;
export const userErrorSelector = (state) => state.users.error;
export const userIdSelector = (state) => {
  let userId;
  const currentUser =
    state && state.users && state.users.user ? state.users.user : null;
  if (currentUser) {
    userId = currentUser.userId;
  } else {
    const localAuth = getLocalStorageAuthentication();
    userId = localAuth ? localAuth.userId : null;
  }
  return userId;
};

export const userIdAndTokenSelector = (state) => {
  let userId;
  let token;
  const currentUser =
    state && state.users && state.users.user ? state.users.user : null;
  if (currentUser) {
    userId = currentUser.userId;
    token = state.users.token;
  } else {
    const localAuth = getLocalStorageAuthentication();
    userId = localAuth ? localAuth.userId : null;
    token = localAuth ? localAuth.token : null;
  }
  return {
    userId,
    token
  };
};

// Thunk action to login user
export const loginUser = 
  ({ email, password, recaptchaToken }) =>
  async (dispatch) => {
    console.log('🔍 [Login Thunk] Attempting login for email:', email);
    
    dispatch(userActionRequest());
    
    try {
      const url = `${BASE_END_POINT_USER}${USER_END_POINT}/auth/token`;
      
      console.log('🌐 [Login Thunk] POST request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, recaptchaToken })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData?.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      const isUserLoginSuccess = data?.data?.user?.id ?? false;
      
      console.log('📦 [Login Thunk] Response data:', data);
      
      // Check if account is not activated
      const errorMsg = data?.message || '';
      if (errorMsg.toLowerCase().includes('not activated')) {
        dispatch(userActionFailure({ error: 'Account not activated' }));
        dispatch(
          messageUpdated({
            text: 'Account not activated. Please check your email for the activation link.',
            severity: Severity.ERROR
          })
        );
        return;
      }
      
      if (!isUserLoginSuccess) {
        throw new Error('Login failed, please try again');
      }
      
      // Normalize the user object to include userId and fullName fields
      // IMPORTANT: Spread userData FIRST to preserve all fields including authorities/roles
      const userData = data.data.user;
      const normalizedUser = {
        ...userData, // Preserve ALL fields from backend (including authorities, roles, etc.)
        userId: userData.id,
        fullName: userData.fullName || userData.full_name || userData.email
      };
      
      console.log('🔍 [Login Thunk] User data from API:', userData);
      console.log('✅ [Login Thunk] Normalized user:', normalizedUser);
      console.log('👥 [Login Thunk] User authorities/roles:', normalizedUser.authorities);
      
      dispatch(
        loginSucceeded({
          token: userData.token,
          user: normalizedUser
        })
      );
      
      // Track login event in Google Analytics
      trackLogin('email');

      // Use random welcome message for successful login based on user role
      dispatch(
        messageUpdated({
          text: getRandomWelcomeMessage(normalizedUser.roles),
          severity: Severity.SUCCESS
        })
      );
      
      return { success: true };
    } catch (error) {
      console.error('❌ [Login Thunk] Failed:', error);
      
      const errorMsg = error.message || 'Login failed, please try again';
      
      dispatch(userActionFailure({ error: errorMsg }));
      dispatch(
        messageUpdated({
          text: errorMsg,
          severity: Severity.ERROR
        })
      );
      
      throw error;
    }
  };

// Thunk action to register user
export const registerUser =
  ({ fullName, email, password, username, recaptchaToken }) =>
  async (dispatch) => {
    console.log('🔍 [Register Thunk] Attempting registration for email:', email);
    
    dispatch(userActionRequest());
    
    try {
      const url = `${BASE_END_POINT_USER}${USER_END_POINT}/register`;
      
      console.log('🌐 [Register Thunk] POST request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fullName, email, password, username, recaptchaToken })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData?.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      const isUserRegistered = data?.data?.id ?? false;
      
      console.log('📦 [Register Thunk] Response data:', data);
      
      // Check if there's an error message in the response (even with 200 status)
      const responseMessage = data?.message || '';
      if (!isUserRegistered && responseMessage) {
        // Backend returned an error message
        dispatch(userActionFailure({ error: responseMessage }));
        dispatch(
          messageUpdated({
            text: responseMessage,
            severity: Severity.ERROR
          })
        );
        return;
      }
      
      // Check if a user is registered
      if (!isUserRegistered) {
        throw new Error('User not registered, please try again');
      }
      
      console.log('✅ [Register Thunk] User registered successfully');
      
      // Track registration event in Google Analytics
      trackSignUp('email');
      
      // Note: Registration might not return a token immediately (email verification required)
      // So we just return success without dispatching registrationSucceeded
      
      dispatch(
        messageUpdated({
          text: 'Registration successful! Please check your email to activate your account.',
          severity: Severity.SUCCESS
        })
      );
      
      return { success: true };
    } catch (error) {
      console.error('❌ [Register Thunk] Failed:', error);
      
      const errorMsg = error.message || 'Registration failed, please try again';
      
      dispatch(userActionFailure({ error: errorMsg }));
      dispatch(
        messageUpdated({
          text: errorMsg,
          severity: Severity.ERROR
        })
      );
      
      throw error;
    }
  };

// Thunk action to update user
export const updateUser =
  ({ user, JWT }) =>
  async (dispatch) => {
    console.log('🔍 [Update User Thunk] Updating user:', user.id);
    
    dispatch(userActionRequest());
    
    try {
      const url = `${BASE_END_POINT_USER}${JOB_END_POINT}/${user.id}`;
      
      console.log('🌐 [Update User Thunk] PUT request to:', url);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${JWT}`
        },
        body: JSON.stringify(user)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      console.log('✅ [Update User Thunk] User updated successfully');
      
      dispatch(userInfoUpdated({ user: data.data || user }));
      
      dispatch(
        messageUpdated({
          text: 'User updated successfully',
          severity: Severity.SUCCESS
        })
      );
      
      return { success: true };
    } catch (error) {
      console.error('❌ [Update User Thunk] Failed:', error);
      
      dispatch(userActionFailure({ error: error.message }));
      dispatch(
        messageUpdated({
          text: 'Failed to update user',
          severity: Severity.ERROR
        })
      );
      
      throw error;
    }
  };

// Thunk action to activate account
export const activateAccount =
  (token) =>
  async (dispatch) => {
    console.log('🔍 [Activate Account Thunk] Activating account with token');
    
    dispatch(userActionRequest());
    
    try {
      const url = `${BASE_END_POINT_USER}${USER_END_POINT}/activate?token=${token}`;
      
      console.log('🌐 [Activate Account Thunk] GET request to:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData?.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      const isActivated = data?.data?.user?.activated === true;
      
      console.log('📦 [Activate Account Thunk] Response data:', data);
      
      if (!isActivated) {
        throw new Error(data?.message || 'Activation failed, please try again');
      }
      
      console.log('✅ [Activate Account Thunk] Account activated successfully');
      
      dispatch(userActionFailure({ error: null })); // Clear any errors
      
      dispatch(
        messageUpdated({
          text: 'Account activated successfully!',
          severity: Severity.SUCCESS
        })
      );
      
      return { success: true, data };
    } catch (error) {
      console.error('❌ [Activate Account Thunk] Failed:', error);
      
      const errorMsg = error.message || 'Activation failed';
      
      dispatch(userActionFailure({ error: errorMsg }));
      dispatch(
        messageUpdated({
          text: errorMsg,
          severity: Severity.ERROR
        })
      );
      
      throw error;
    }
  };

// Thunk action to resend activation email
export const resendActivationEmail =
  (email) =>
  async (dispatch) => {
    console.log('🔍 [Resend Activation Thunk] Resending activation email to:', email);
    
    dispatch(userActionRequest());
    
    try {
      const url = `${BASE_END_POINT_USER}${USER_END_POINT}/resend-activation`;
      
      console.log('🌐 [Resend Activation Thunk] POST request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData?.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      const message = data?.data?.message || data?.message || 'Activation email sent!';
      
      console.log('✅ [Resend Activation Thunk] Email sent successfully');
      
      dispatch(userActionFailure({ error: null })); // Clear any errors
      
      dispatch(
        messageUpdated({
          text: message,
          severity: Severity.SUCCESS
        })
      );
      
      return { success: true, data };
    } catch (error) {
      console.error('❌ [Resend Activation Thunk] Failed:', error);
      
      const errorMsg = error.message || 'Failed to resend activation email';
      
      dispatch(userActionFailure({ error: errorMsg }));
      dispatch(
        messageUpdated({
          text: errorMsg,
          severity: Severity.ERROR
        })
      );
      
      throw error;
    }
};

// Thunk action to add user role (Company or Candidate)
export const addUserRole =
  (role) =>
  async (dispatch) => {
    console.log('🔍 [Add User Role Thunk] Adding role:', role);
    
    dispatch(userActionRequest());
    
    try {
      const localAuth = getLocalStorageAuthentication();
      
      if (!localAuth || !localAuth.token) {
        throw new Error('No authentication token found');
      }
      
      const url = `${BASE_END_POINT_USER}${USER_END_POINT}/roles`;
      
      console.log('🌐 [Add User Role Thunk] POST request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify({ role })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData?.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      const newToken = data?.data?.accessToken;
      
      console.log('📦 [Add User Role Thunk] Response data:', data);
      console.log('🔑 [Add User Role Thunk] New token:', newToken ? 'received' : 'missing');
      
      if (!newToken) {
        throw new Error('No token returned from server');
      }
      
      // Save the new JWT with updated roles
      saveJWTInfoToLocalStorage(newToken);
      
      // Decode the new JWT to get updated user info with roles
      const updatedAuth = getLocalStorageAuthentication();
      
      if (!updatedAuth) {
        throw new Error('Failed to decode updated token');
      }
      
      console.log('👥 [Add User Role Thunk] Updated user roles:', updatedAuth.roles);
      
      // Update Redux state with new token and user info including roles
      dispatch(
        loginSucceeded({
          token: newToken,
          user: {
            userId: updatedAuth.userId,
            fullName: updatedAuth.fullName,
            email: updatedAuth.email,
            roles: updatedAuth.roles
          }
        })
      );
      
      const userType = role === 'ROLE_COMPANY' ? 'Company' : 'Candidate';
      
      dispatch(
        messageUpdated({
          text: `Welcome! You're all set as a ${userType}!`,
          severity: Severity.SUCCESS
        })
      );
      
      console.log('✅ [Add User Role Thunk] User role added successfully');
      
      return { success: true, roles: updatedAuth.roles };
    } catch (error) {
      console.error('❌ [Add User Role Thunk] Failed:', error);
      
      const errorMsg = error.message || 'Failed to update user type';
      
      dispatch(userActionFailure({ error: errorMsg }));
      dispatch(
        messageUpdated({
          text: errorMsg,
          severity: Severity.ERROR
        })
      );
      
      throw error;
    }
  };
